/**
 * The measure bars for the editor's selection: the room's width and length
 * (with each wall's deepest alcove when nothing is selected), a chamfer's
 * legs, a segment's position and width along its wall plus an alcove's
 * depth, a free element's gap / size / gap, and where each bar draws.
 */

import {
  alcoveExtendedBounds,
  EDGE_NAMES,
  edgeSegments,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  geometryBounds,
  type PlanBounds,
  type Point,
  type ResolvedLine,
  resolveLineRef,
  worldOnLine,
} from '@kaiizen/kai-floor-plan';
import { chamferData, displayShape } from '../editorGeometry';
import type { ChamferData, EditorMutableState, MeasureBar, Rect } from '../editorState';
import { type BarScreen, barsOnStage, measureBarScreen, type PlacedBar } from './measureBarLayout';
import { dimBarSides } from './selectionGeometry';

/** The room extent free-element positions are measured against, by display and gap-chip edits alike. */
export { alcoveExtendedBounds };

/** The chip keys of a gap / size / gap family, three per axis. */
interface GapSizeGapKeys {
  left: string;
  width: string;
  right: string;
  top: string;
  length: string;
  bottom: string;
}

/**
 * Six measure bars, three per axis: the gap from `region`'s minimum to `box`,
 * the box's own extent (emphasized), and the gap from the box to the
 * region's maximum. `gapValue` turns a signed gap into the value its chip
 * shows. Shared by the editor's free element and the assembler's room.
 */
export function gapSizeGapBars(
  box: PlanBounds,
  region: PlanBounds,
  keys: GapSizeGapKeys,
  gapValue: (gap: number) => number,
  extent = { width: box.maxX - box.minX, length: box.maxY - box.minY },
): MeasureBar[] {
  return [
    { type: 'h', from: region.minX, to: box.minX, at: box.minY, value: gapValue(box.minX - region.minX), key: keys.left },
    { type: 'h', from: box.minX, to: box.maxX, at: box.minY, value: extent.width, key: keys.width, emphasized: true },
    { type: 'h', from: box.maxX, to: region.maxX, at: box.minY, value: gapValue(region.maxX - box.maxX), key: keys.right },
    { type: 'v', from: region.minY, to: box.minY, at: box.minX, value: gapValue(box.minY - region.minY), key: keys.top },
    { type: 'v', from: box.minY, to: box.maxY, at: box.minX, value: extent.length, key: keys.length, emphasized: true },
    { type: 'v', from: box.maxY, to: region.maxY, at: box.minX, value: gapValue(region.maxY - box.maxY), key: keys.bottom },
  ];
}

/** A bar between two points on a line: horizontal lines measure x, vertical ones y. */
const alongBar = (horizontal: boolean, from: Point, to: Point, value: number, key: string): MeasureBar =>
  horizontal
    ? { type: 'h', from: from.x, to: to.x, at: from.y, value, key }
    : { type: 'v', from: from.y, to: to.y, at: from.x, value, key };

/**
 * A segment measured along its line: the distance from the line's usable
 * start, its own width (emphasized), the distance to the usable end, and,
 * for an alcove, a bar across its depth.
 */
function segmentBars(line: ResolvedLine, segment: FloorPlanEdgeSegment, depth: number, widthKey: string, depthKey: string): MeasureBar[] {
  const { lf: lineFrame, minBound: edgeStart } = line;
  const edgeEnd = line.maxBound || lineFrame.length;
  const { position, width } = segment;
  const horizontal = Math.abs(lineFrame.dir.x) > 0.5;
  const segmentStart = worldOnLine(lineFrame, position, 0), segmentEnd = worldOnLine(lineFrame, position + width, 0);
  const bars: MeasureBar[] = [
    alongBar(horizontal, worldOnLine(lineFrame, edgeStart, 0), segmentStart, position - edgeStart, 'seg-from-start'),
    { ...alongBar(horizontal, segmentStart, segmentEnd, width, widthKey), emphasized: true },
    alongBar(horizontal, segmentEnd, worldOnLine(lineFrame, edgeEnd, 0), edgeEnd - (position + width), 'seg-from-end'),
  ];
  if (depth > 1e-6) {
    const corners = [segmentStart, worldOnLine(lineFrame, position + width, depth), worldOnLine(lineFrame, position, depth), segmentEnd];
    const minX = Math.min(...corners.map(corner => corner.x)), maxX = Math.max(...corners.map(corner => corner.x));
    const minY = Math.min(...corners.map(corner => corner.y)), maxY = Math.max(...corners.map(corner => corner.y));
    bars.push(
      horizontal
        ? { type: 'v', from: minY, to: maxY, at: minX, value: depth, key: depthKey }
        : { type: 'h', from: minX, to: maxX, at: minY, value: depth, key: depthKey },
    );
  }
  return bars;
}

/** The room's width and length, plus with nothing selected the deepest alcove on each wall. */
function roomBars(room: FloorPlanRoom, withAlcoveDepths: boolean): MeasureBar[] {
  const bars: MeasureBar[] = [
    { type: 'h', from: 0, to: room.width, at: 0, value: room.width, key: 'width' },
    { type: 'v', from: 0, to: room.length, at: 0, value: room.length, key: 'length' },
  ];
  if (!withAlcoveDepths) return bars;
  for (const edge of EDGE_NAMES) {
    const depths = edgeSegments(room, edge).filter(segment => segment.edgeSegmentType === 'alcove').map(segment => segment.depth ?? 0);
    const maxDepth = depths.length ? Math.max(...depths) : 0;
    if (maxDepth < 1e-6) continue;
    const key = `maxdepth:${edge}`;
    if (edge === 'backEdge') bars.push({ type: 'v', from: -maxDepth, to: 0, at: 0, value: maxDepth, key });
    else if (edge === 'frontEdge') bars.push({ type: 'v', from: room.length, to: room.length + maxDepth, at: 0, value: maxDepth, key });
    else if (edge === 'leftEdge') bars.push({ type: 'h', from: -maxDepth, to: 0, at: 0, value: maxDepth, key });
    else bars.push({ type: 'h', from: room.width, to: room.width + maxDepth, at: 0, value: maxDepth, key });
  }
  return bars;
}

/** A chamfer's two legs, from its corner. */
const chamferBars = ({ corner, sx, sy, ch }: ChamferData): MeasureBar[] => [
  { type: 'h', from: corner.x, to: corner.x + sx * ch.dx, at: corner.y, value: ch.dx, key: 'chamferdx' },
  { type: 'v', from: corner.y, to: corner.y + sy * ch.dy, at: corner.x, value: ch.dy, key: 'chamferdy' },
];

/**
 * A line segment's bars: one bar along a chamfer diagonal, else measured
 * along its wall, with depth for an alcove on a primary edge.
 */
function lineSegmentBars(line: ResolvedLine, segment: FloorPlanEdgeSegment): MeasureBar[] {
  const { ref, lf: lineFrame } = line;
  const isDiagonal = ref.k !== 'pe' && ref.k !== 'aw' && Math.abs(lineFrame.dir.x) > 0.01 && Math.abs(lineFrame.dir.y) > 0.01;
  if (isDiagonal) {
    return [{ type: 'd', from: segment.position, to: segment.position + segment.width, at: 0, value: segment.width, key: 'seg-width', lf: lineFrame }];
  }
  const depth = ref.k === 'pe' && segment.edgeSegmentType === 'alcove' ? segment.depth ?? 0 : 0;
  return segmentBars(line, segment, depth, 'seg-width', 'seg-depth');
}

/**
 * The measure bars for what's selected, over the displayed room (the draft
 * mid-drag), and the frame they measure. A selected alcove wall shows the
 * same set as the whole alcove: the wall's own length is one of them (width
 * for the back wall, depth for the side walls), and the perpendicular bars
 * stay visible while the wall is dragged. A free element's bars span the
 * full room extent including alcoves, and a gap shows its magnitude when the
 * element overhangs that extent.
 */
export function computeDims(state: EditorMutableState): { frame: Rect; bars: MeasureBar[] } {
  const room = displayShape(state);
  const selection = state.selection;
  const roomFrame: Rect = { x: 0, y: 0, w: room.width, h: room.length };

  if (selection.kind === 'edge' || selection.kind === 'room' || selection.kind === 'none') {
    return { frame: roomFrame, bars: roomBars(room, selection.kind === 'none') };
  }
  if (selection.kind === 'corner' || selection.kind === 'alcoveCorner' || selection.kind === 'innerCorner') {
    const chamfer = chamferData(state, selection);
    return chamfer ? { frame: chamfer.frame, bars: chamferBars(chamfer) } : { frame: roomFrame, bars: [] };
  }
  if (selection.kind === 'alcove' || selection.kind === 'alcoveWall') {
    const line = resolveLineRef(room, { k: 'pe', edge: selection.edge });
    const alcove = line?.segments[selection.index];
    if (!line || !alcove) return { frame: roomFrame, bars: [] };
    const key = `alcove:${selection.edge}:${selection.index}`;
    return { frame: roomFrame, bars: segmentBars(line, alcove, alcove.depth ?? 0, `${key}:width`, `${key}:depth`) };
  }
  if (selection.kind === 'lineSeg') {
    const line = resolveLineRef(room, selection.ref);
    const segment = line?.segments[selection.si];
    return line && segment ? { frame: roomFrame, bars: lineSegmentBars(line, segment) } : { frame: roomFrame, bars: [] };
  }
  const element = room.freeElements?.[selection.index];
  if (!element) return { frame: roomFrame, bars: [] };
  return {
    frame: { x: element.x, y: element.y, w: element.width, h: element.length },
    bars: gapSizeGapBars(
      { minX: element.x, minY: element.y, maxX: element.x + element.width, maxY: element.y + element.length },
      alcoveExtendedBounds(room),
      {
        left: 'gap-left',
        width: `inner:${selection.index}:width`,
        right: 'gap-right',
        top: 'gap-top',
        length: `inner:${selection.index}:height`,
        bottom: 'gap-bottom',
      },
      Math.abs,
      { width: element.width, length: element.length },
    ),
  };
}

/** Where one of the editor's measure bars draws on screen. */
export function barScreen(state: EditorMutableState, bar: MeasureBar): BarScreen {
  return measureBarScreen(state.viewport, geometryBounds(displayShape(state)), dimBarSides(state), bar, state.inputKind);
}

/**
 * The editor's measure bars where they draw on a `stageW` × `stageH` stage,
 * each family held on it (`barsOnStage`). The bar lines and their chips both
 * draw from this, so they move together while the view is zoomed in.
 */
export function placedBars(state: EditorMutableState, stageW: number, stageH: number): PlacedBar[] {
  return barsOnStage(computeDims(state).bars.map(bar => ({ bar, g: barScreen(state, bar) })), stageW, stageH);
}
